"use client";

import Link from "next/link";
import { useEffect, useMemo, useState } from "react";
import {
  CONSENT_UPDATED_EVENT,
  hasSeenConsentPrompt,
  markConsentPromptSeen,
  writeTrackingConsentSnapshot,
} from "@/lib/consent/client";
import { DEFAULT_CONSENT_PREFERENCES } from "@/lib/consent/defaults";
import type { ConsentPreferences, ConsentResponse } from "@/lib/consent/types";
import { getSupabaseBrowserClient } from "@/lib/supabase/client";

function ToggleRow({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled?: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    <label className="flex items-start justify-between gap-4 rounded-2xl border border-white/10 bg-white/5 px-4 py-3">
      <span className="min-w-0">
        <span className="block text-sm font-medium text-[var(--text)]">{label}</span>
        <span className="mt-1 block text-xs text-[var(--muted)]">{description}</span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={Boolean(disabled)}
        onChange={(event) => onChange(event.target.checked)}
        className="mt-1 h-4 w-4 rounded border-white/20 bg-white/10 text-[var(--accent)] focus:ring-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-60"
      />
    </label>
  );
}

function withTrackingConstraints(preferences: ConsentPreferences): ConsentPreferences {
  if (!preferences.analytics_tracking) {
    return { ...preferences, session_replay: false };
  }
  if (preferences.session_replay) {
    return { ...preferences, analytics_tracking: true };
  }
  return preferences;
}

export default function ConsentFirstVisitPrompt() {
  const [isVisible, setIsVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [loadedPreferences, setLoadedPreferences] = useState<ConsentPreferences>({
    ...DEFAULT_CONSENT_PREFERENCES,
  });
  const [preferences, setPreferences] = useState<ConsentPreferences>({
    ...DEFAULT_CONSENT_PREFERENCES,
  });

  useEffect(() => {
    let active = true;
    const supabase = getSupabaseBrowserClient();

    const load = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (!active || !session) {
          if (active) setIsVisible(false);
          return;
        }
        const response = await fetch("/api/account/consents", { cache: "no-store" });
        if (!response.ok) {
          if (active) setIsVisible(false);
          return;
        }
        const payload = (await response.json()) as ConsentResponse;
        if (!active) return;
        setLoadedPreferences(payload.preferences);
        setPreferences(payload.preferences);
        const shouldPrompt =
          payload.source === "system_default" && !hasSeenConsentPrompt();
        setIsVisible(shouldPrompt);
      } catch {
        if (active) setIsVisible(false);
      }
    };

    void load();
    return () => {
      active = false;
    };
  }, []);

  const constrainedPreferences = useMemo(
    () => withTrackingConstraints(preferences),
    [preferences]
  );

  const dismissPrompt = () => {
    markConsentPromptSeen();
    setIsVisible(false);
    setIsModalOpen(false);
    setErrorMessage(null);
  };

  const persistPreferences = async (
    source: string,
    nextPreferences: ConsentPreferences = preferences
  ) => {
    if (isSaving) return;
    setIsSaving(true);
    setErrorMessage(null);
    const constrainedNextPreferences = withTrackingConstraints(nextPreferences);

    try {
      const response = await fetch("/api/account/consents", {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...constrainedNextPreferences,
          source,
        }),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.error || "consent_update_failed");
      }

      const payload = (await response.json()) as ConsentResponse;
      setLoadedPreferences(payload.preferences);
      setPreferences(payload.preferences);
      writeTrackingConsentSnapshot(payload.preferences);
      window.dispatchEvent(
        new CustomEvent(CONSENT_UPDATED_EVENT, {
          detail: payload.preferences,
        })
      );
      markConsentPromptSeen();
      setIsVisible(false);
      setIsModalOpen(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "consent_update_failed";
      setErrorMessage(`Could not save consent settings (${message}).`);
    } finally {
      setIsSaving(false);
    }
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <div className="fixed inset-x-4 bottom-4 z-50 mx-auto w-full max-w-3xl rounded-3xl border border-white/15 bg-[var(--bg-alt)]/95 p-4 shadow-2xl backdrop-blur-xl">
        <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Consent</p>
        <p className="mt-2 text-sm text-[var(--text)]">
          Tracking and communication preferences are enabled by default. Review
          them now if you want to turn anything off.
        </p>
        <p className="mt-1 text-xs text-[var(--muted)]">
          You can always edit this in{" "}
          <Link href="/account/settings" className="text-[var(--accent)] hover:text-[var(--accent-2)]">
            account settings
          </Link>
          .
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => {
              setIsModalOpen(true);
              setErrorMessage(null);
            }}
            className="rounded-full border border-white/25 bg-white/5 px-4 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/45"
          >
            Review preferences
          </button>
          <button
            type="button"
            onClick={() =>
              void persistPreferences("consent_banner", DEFAULT_CONSENT_PREFERENCES)
            }
            disabled={isSaving}
            className="rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/15 px-4 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--accent)] transition hover:border-[var(--accent)]/70 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isSaving ? "Saving..." : "Use defaults"}
          </button>
          <button
            type="button"
            onClick={dismissPrompt}
            disabled={isSaving}
            className="rounded-full border border-transparent px-3 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--muted)] transition hover:text-[var(--text)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            Not now
          </button>
        </div>
        {errorMessage ? (
          <p className="mt-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
            {errorMessage}
          </p>
        ) : null}
      </div>

      {isModalOpen ? (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Consent preferences"
            className="max-h-[92vh] w-full max-w-2xl overflow-auto rounded-3xl border border-white/15 bg-[var(--bg-alt)] p-5"
          >
            <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">Consent preferences</p>
            <p className="mt-2 text-sm text-[var(--muted)]">
              Choose how deepface.dev can track usage and send product communication.
            </p>

            <div className="mt-5 space-y-3">
              <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">
                Tracking
              </p>
              <ToggleRow
                label="Analytics tracking"
                description="Allow anonymized product analytics and page telemetry."
                checked={constrainedPreferences.analytics_tracking}
                disabled={isSaving}
                onChange={(next) =>
                  setPreferences((prev) =>
                    withTrackingConstraints({
                      ...prev,
                      analytics_tracking: next,
                      session_replay: next ? prev.session_replay : false,
                    })
                  )
                }
              />
              <ToggleRow
                label="Session replay"
                description="Allow session replay capture for support and debugging."
                checked={constrainedPreferences.session_replay}
                disabled={isSaving || !constrainedPreferences.analytics_tracking}
                onChange={(next) =>
                  setPreferences((prev) =>
                    withTrackingConstraints({
                      ...prev,
                      analytics_tracking: next ? true : prev.analytics_tracking,
                      session_replay: next,
                    })
                  )
                }
              />
            </div>

            <div className="mt-5 space-y-3">
              <p className="text-2xs uppercase tracking-[0.3em] text-[var(--muted)]">
                Emails
              </p>
              <ToggleRow
                label="Product updates"
                description="Receive release notes, operational updates, and service announcements."
                checked={constrainedPreferences.product_updates_email}
                disabled={isSaving}
                onChange={(next) =>
                  setPreferences((prev) => ({ ...prev, product_updates_email: next }))
                }
              />
              <ToggleRow
                label="Marketing emails"
                description="Receive news, campaigns, and promotional content."
                checked={constrainedPreferences.marketing_email}
                disabled={isSaving}
                onChange={(next) =>
                  setPreferences((prev) => ({ ...prev, marketing_email: next }))
                }
              />
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => {
                  setPreferences(loadedPreferences);
                  setIsModalOpen(false);
                  setErrorMessage(null);
                }}
                disabled={isSaving}
                className="rounded-full border border-white/20 bg-white/5 px-4 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--text)] transition hover:border-white/40 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void persistPreferences("consent_banner")}
                disabled={isSaving}
                className="rounded-full border border-[var(--accent)]/40 bg-[var(--accent)]/15 px-4 py-2 text-2xs font-semibold uppercase tracking-[0.3em] text-[var(--accent)] transition hover:border-[var(--accent)]/70 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {isSaving ? "Saving..." : "Save preferences"}
              </button>
            </div>

            {errorMessage ? (
              <p className="mt-3 rounded-xl border border-amber-400/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-100">
                {errorMessage}
              </p>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
